import { makeStyles } from '@material-ui/core'

export const useStyles = makeStyles(() => ({
  '@global': {
    '@keyframes pulse': {
      '0%, 100%': { opacity: 1 },
      '50%': { opacity: 0.3 }
    }
  },

  // -- TestsPanel (container + toolbar + detail area) --
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden'
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 'var(--padding-sm) var(--padding-md)',
    borderBottom: '1px solid var(--color-neutral-200)',
    flexShrink: 0
  },
  summary: {
    fontSize: 'var(--text-size-xs)',
    color: 'var(--color-neutral-500)',
    fontWeight: 500
  },
  toolbarActions: {
    display: 'flex',
    gap: 'var(--gap-xs)'
  },
  runMenuWrapper: {
    position: 'relative'
  },
  runMenu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '4px',
    backgroundColor: 'white',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    boxShadow: 'var(--shadow-md)',
    zIndex: 100,
    overflow: 'hidden',
    minWidth: '170px'
  },
  menuItem: {
    display: 'block',
    width: '100%',
    padding: '8px 14px',
    border: 'none',
    backgroundColor: 'transparent',
    textAlign: 'left',
    fontSize: 'var(--text-size-sm)',
    color: 'var(--color-neutral-700)',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-050)'
    }
  },
  menuDivider: {
    height: '1px',
    backgroundColor: 'var(--color-neutral-200)',
    margin: '2px 0'
  },
  detailArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    borderTop: '2px solid var(--color-neutral-200)'
  },
  detailEmpty: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-neutral-400)',
    fontSize: 'var(--text-size-sm)',
    borderTop: '2px solid var(--color-neutral-200)'
  },
  detailHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px var(--padding-md)',
    borderBottom: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)',
    flexShrink: 0
  },
  detailTitle: {
    fontSize: 'var(--text-size-sm)',
    fontWeight: 600,
    color: 'var(--color-neutral-700)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  detailResultStatus: {
    fontSize: 'var(--text-size-xs)',
    fontWeight: 600
  },
  detailTabs: {
    display: 'flex',
    borderBottom: '1px solid var(--color-neutral-200)',
    flexShrink: 0
  },
  detailTab: {
    padding: '6px 14px',
    fontSize: 'var(--text-size-xs)',
    fontWeight: 500,
    cursor: 'pointer',
    color: 'var(--color-neutral-500)',
    borderBottom: '2px solid transparent',
    '&:hover': {
      color: 'var(--color-neutral-800)'
    }
  },
  detailTabActive: {
    color: 'var(--color-primary-700)',
    borderBottomColor: 'var(--color-primary-500)',
    fontWeight: 600
  },
  detailContent: {
    flex: 1,
    overflow: 'auto',
    minHeight: 0
  },
  failDot: {
    display: 'inline-block',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: 'var(--color-danger-500)',
    marginLeft: '5px',
    verticalAlign: 'middle'
  },

  // -- TestList --
  testList: {
    flexShrink: 0,
    maxHeight: '35%',
    overflow: 'auto'
  },
  testRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px var(--padding-md)',
    gap: 'var(--gap-sm)',
    cursor: 'pointer',
    borderBottom: '1px solid var(--color-neutral-100)',
    transition: 'background-color var(--transition-duration-short)',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-050)'
    },
    '&:hover $rowActions': {
      opacity: 1
    }
  },
  testRowSelected: {
    backgroundColor: 'var(--color-primary-050)',
    '&:hover': {
      backgroundColor: 'var(--color-primary-050)'
    }
  },
  statusIndicator: {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    flexShrink: 0
  },
  statusPassed: { backgroundColor: 'var(--color-success-500)' },
  statusFailed: { backgroundColor: 'var(--color-danger-500)' },
  statusNeutral: { backgroundColor: 'var(--color-neutral-400)' },
  modeBadge: {
    padding: '1px 6px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    flexShrink: 0
  },
  testName: {
    flex: 1,
    fontSize: 'var(--text-size-sm)',
    fontWeight: 500,
    color: 'var(--color-neutral-800)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0
  },
  testMeta: {
    fontSize: 'var(--text-size-xs)',
    color: 'var(--color-neutral-400)',
    flexShrink: 0
  },
  rowActions: {
    display: 'flex',
    gap: '2px',
    flexShrink: 0,
    opacity: 0
  },
  iconBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '22px',
    height: '22px',
    border: 'none',
    borderRadius: 'var(--border-radius-sm)',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '12px',
    color: 'var(--color-neutral-500)',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-200)',
      color: 'var(--color-neutral-800)'
    }
  },
  iconBtnDanger: {
    '&:hover': {
      backgroundColor: 'var(--color-danger-050)',
      color: 'var(--color-danger-600)'
    }
  },

  // -- TestConfig --
  configPanel: {
    padding: 'var(--padding-md)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--gap-md)'
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  fieldRow: {
    display: 'flex',
    gap: 'var(--gap-md)',
    alignItems: 'center'
  },
  fieldLabel: {
    fontSize: 'var(--text-size-xs)',
    fontWeight: 'var(--text-weight-bold)',
    color: 'var(--color-neutral-500)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  nameInput: {
    padding: '6px 10px',
    height: '30px',
    boxSizing: 'border-box',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-sm)',
    outline: 'none',
    backgroundColor: 'white',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    }
  },
  modeSelector: {
    display: 'flex',
    borderRadius: 'var(--border-radius-sm)',
    overflow: 'hidden',
    border: '1px solid var(--color-neutral-300)',
    alignSelf: 'flex-start'
  },
  modeOption: {
    padding: '4px 12px',
    fontSize: '11px',
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
    backgroundColor: 'white',
    color: 'var(--color-neutral-500)',
    transition: 'all var(--transition-duration-short)',
    '&:not(:last-child)': {
      borderRight: '1px solid var(--color-neutral-300)'
    },
    '&:hover': {
      backgroundColor: 'var(--color-neutral-050)'
    }
  },
  modeOptionActive: {
    color: 'white',
    '&:hover': {
      filter: 'brightness(1.1)'
    }
  },
  aceWrapper: {
    border: '1px solid var(--color-neutral-200)',
    borderRadius: 'var(--border-radius-sm)',
    overflow: 'hidden'
  },
  overrideBadgeRow: {
    display: 'flex',
    gap: 'var(--gap-sm)',
    flexWrap: 'wrap'
  },
  overrideBadgeQuery: {
    padding: '2px 8px',
    fontSize: '11px',
    fontWeight: 600,
    borderRadius: 'var(--border-radius-sm)',
    backgroundColor: 'var(--color-primary-050)',
    color: 'var(--color-primary-700)',
    border: '1px solid var(--color-primary-200)'
  },
  overrideBadgeCommand: {
    padding: '2px 8px',
    fontSize: '11px',
    fontWeight: 600,
    borderRadius: 'var(--border-radius-sm)',
    backgroundColor: 'var(--color-danger-050)',
    color: 'var(--color-danger-700)',
    border: '1px solid var(--color-danger-200)'
  },
  overrideBadgePolicy: {
    padding: '2px 8px',
    fontSize: '11px',
    fontWeight: 600,
    borderRadius: 'var(--border-radius-sm)',
    backgroundColor: 'var(--color-warning-050)',
    color: 'var(--color-warning-700)',
    border: '1px solid var(--color-warning-200)'
  },

  // -- TestOutput --
  outputPanel: {
    padding: 'var(--padding-sm) var(--padding-md)'
  },
  outputBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--gap-sm)',
    padding: '8px 12px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-sm)',
    fontWeight: 600,
    marginBottom: 'var(--margin-sm)'
  },
  bannerPass: {
    backgroundColor: 'var(--color-success-050)',
    color: 'var(--color-success-700)',
    border: '1px solid var(--color-success-200)'
  },
  bannerFail: {
    backgroundColor: 'var(--color-danger-050)',
    color: 'var(--color-danger-700)',
    border: '1px solid var(--color-danger-200)'
  },
  assertionError: {
    padding: '8px 12px',
    marginBottom: 'var(--margin-sm)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '12px',
    fontFamily: 'var(--font-mono)',
    color: 'var(--color-danger-700)',
    backgroundColor: 'var(--color-danger-050)',
    border: '1px solid var(--color-danger-200)',
    lineHeight: 1.5
  },
  resultWrapper: {
    position: 'relative'
  },
  resultPre: {
    margin: 0,
    padding: '10px 12px',
    fontSize: '12px',
    fontFamily: 'var(--font-mono)',
    whiteSpace: 'pre-wrap',
    color: 'var(--color-neutral-700)',
    lineHeight: 1.5,
    backgroundColor: 'var(--color-neutral-050)',
    borderRadius: 'var(--border-radius-sm)',
    border: '1px solid var(--color-neutral-200)',
    maxHeight: '300px',
    overflow: 'auto'
  },
  resultCopyBtn: {
    position: 'absolute',
    top: '6px',
    right: '6px',
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid var(--color-neutral-200)',
    borderRadius: 'var(--border-radius-sm)',
    backgroundColor: 'white',
    cursor: 'pointer',
    fontSize: '12px',
    color: 'var(--color-neutral-400)',
    opacity: 0,
    transition: 'opacity 0.15s ease',
    '$resultWrapper:hover &': {
      opacity: 1
    },
    '&:hover': {
      color: 'var(--color-neutral-700)',
      backgroundColor: 'var(--color-neutral-050)'
    }
  },
  noOutput: {
    padding: 'var(--padding-lg)',
    textAlign: 'center',
    color: 'var(--color-neutral-400)',
    fontSize: 'var(--text-size-sm)'
  },
  outputTabBar: {
    display: 'flex',
    borderBottom: '1px solid var(--color-neutral-200)',
    flexShrink: 0
  }
}))
